import { gql } from '@apollo/client';

/**
 * Match review (card 23, on card 07's API): ilbp-backend
 * docs/CARD_23_MATCH_REVIEW.md and CARD_07_FUZZY_REVIEW_QUEUE.md §3. Fields
 * are written out in each document, not shared as fragments: the tests run
 * without __typename (CLAUDE.md).
 */

/**
 * One page of one tab, its counts per rule (`byRule`, for the rule chips),
 * and how many each tab holds (`waiting`, `approved`, `rejected`). The queue
 * is small, tens of rows, so the tab counts come with every page.
 */
export const CRM_MATCH_REVIEW = gql`
  query CrmMatchReview($review: MatchReview!, $rule: Int, $skip: Int, $take: Int) {
    nbMatchReviewQueue(review: $review, rule: $rule, skip: $skip, take: $take) {
      total
      byRule {
        rule
        count
      }
      items {
        nbSignupId
        rule
        review
        reason
        userId
        candidateCount
        sameAccountAs
        corroboratedBy
        contactId
        reviewedAt
        reviewNote
        decidedAt
        merge {
          id
          fromContactId
          intoContactId
          mergedAt
          mergedBy {
            id
            firstName
            lastName
          }
          results {
            key
            label
            changed
          }
        }
        mergeable
        reviewedBy {
          id
          firstName
          lastName
        }
        signup {
          id
          firstName
          middleName
          lastName
          emails
          phones {
            type
            number
            display
            dialCode
          }
          address1
          city
          state
          zip
          countryCode
          externalId
          isPossibleDuplicate
          createdAt
        }
        candidates {
          userId
          firstName
          lastName
          username
          email
          phone
          streetAddress
          city
          state
          countryCode
          createdAt
          isDeleted
          heldBy
          approvable
          contactId
        }
      }
    }
    waiting: nbMatchReviewQueue(review: PENDING, take: 1) {
      total
    }
    approved: nbMatchReviewQueue(review: APPROVED, take: 1) {
      total
    }
    rejected: nbMatchReviewQueue(review: REJECTED, take: 1) {
      total
    }
  }
`;

/**
 * Approve, reject or reopen one row. Takes the state to set, so a retry lands
 * on the same answer. Approving merges the two contacts, and reopening undoes
 * it (card 28). `userId` is the account chosen among several; `note` is
 * required to approve a name-and-town match (rule 6).
 */
export const SET_NB_MATCH_REVIEW = gql`
  mutation SetNbMatchReview($nbSignupId: Int!, $review: MatchReview!, $userId: Int, $note: String) {
    setNbMatchReview(nbSignupId: $nbSignupId, review: $review, userId: $userId, note: $note) {
      nbSignupId
      review
      userId
      reviewedAt
      reviewNote
      contactId
      mergeable
      merge {
        id
        intoContactId
      }
    }
  }
`;

/**
 * Card 28: joins an approved match's two contacts, for a match approved before
 * merging was built (`mergeable`). Approving merges by itself.
 */
export const MERGE_NB_MATCH = gql`
  mutation MergeNbMatch($nbSignupId: Int!) {
    mergeNbMatch(nbSignupId: $nbSignupId) {
      nbSignupId
      contactId
      mergeable
      merge {
        id
        intoContactId
      }
    }
  }
`;

/** The count beside the sidebar's Match Review entry: rows waiting for a person. */
export const CRM_MATCH_REVIEW_WAITING = gql`
  query CrmMatchReviewWaiting {
    nbMatchReviewQueue(review: PENDING, take: 1) {
      total
    }
  }
`;
